import { useEffect, useRef, useState } from 'react';
import { ChatSocket, type ChatMessage } from '@/lib/chat-socket';

interface ChatPanelProps {
  bookingId: string;
  accessToken: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function ChatPanel({ bookingId, accessToken, open: controlledOpen, onOpenChange }: ChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const bottomRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<ChatSocket | null>(null);

  useEffect(() => {
    if (!open) return;

    const sock = new ChatSocket(accessToken);
    socketRef.current = sock;

    sock
      .onConnect(() => {
        setConnected(true);
        setError(null);
        sock.joinRoom(bookingId);
      })
      .onHistory((msgs) => setMessages(msgs))
      .onMessage((msg) => setMessages((prev) => [...prev, msg]))
      .onError((e) => setError(e.message))
      .connect();

    return () => {
      sock.offAll();
      sock.disconnect();
      socketRef.current = null;
      setConnected(false);
      setMessages([]);
    };
  }, [open, bookingId, accessToken]);

  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  function handleSend() {
    const body = input.trim().slice(0, 2000);
    if (!body || !socketRef.current) return;
    socketRef.current.sendMessage(bookingId, body);
    setInput('');
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const handleToggle = () => {
    if (isControlled) {
      onOpenChange?.(!open);
    } else {
      setUncontrolledOpen((v) => !v);
    }
  };

  return (
    <>
      {!isControlled && (
        <div className="mt-3 border-t border-slate-200 pt-3">
          <button
            onClick={handleToggle}
            className="text-xs font-medium text-blue-600 hover:underline cursor-pointer"
          >
            {open ? 'Hide chat' : 'Open chat'}
          </button>
        </div>
      )}

      {open && (
        <div className={`flex flex-col rounded-md border border-slate-200 bg-slate-50 ${isControlled ? '' : 'mt-2'}`}>
          <div className="h-56 overflow-y-auto p-3 space-y-2">
            {!connected && !error && (
              <p className="text-xs text-slate-400">Connecting…</p>
            )}
            {error && (
              <p className="text-xs text-red-500">{error}</p>
            )}
            {messages.length === 0 && connected && (
              <p className="text-xs text-slate-400">No messages yet.</p>
            )}
            {messages.map((m) => (
              <div key={m.id} className="flex flex-col">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  {m.senderRole}
                </span>
                <p className="mt-0.5 rounded-md bg-white px-2 py-1.5 text-sm text-slate-800 shadow-sm border border-slate-100 w-fit max-w-[85%]">
                  {m.body}
                </p>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-slate-200 p-2 flex gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message… (Enter to send)"
              rows={1}
              disabled={!connected}
              className="flex-1 resize-none rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50"
            />
            <button
              onClick={handleSend}
              disabled={!connected || !input.trim()}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50 cursor-pointer"
            >
              Send
            </button>
          </div>
        </div>
      )}
    </>
  );
}
